// src/pages/FacturacionPresupuestos/imprimirDocumentoService.js
// Servicio de impresión aislado para documentos comerciales (Presupuestos, Remisiones, Notas de Pago)
// Genera un documento HTML puro en un iframe aislado, garantizando 0 interferencia de elementos de la UI.

import { numeroALetras } from '../../utils/numeroALetras';
import { TEMAS_ZEN, FUENTES_DISPONIBLES } from '../../utils/plantillasDocumentos';
import { obtenerTamanoContenidoSeccion, obtenerTamanoTituloSeccion } from './tamanoTextoUtils';
import { formatearTextoEnriquecidoHtml } from '../../utils/formatoTextoUtils';
import { sincronizarTextoInversionInicial } from '../../utils/inversionInicialUtils';

function escapeHtml(str) {
    if (str === undefined || str === null) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

export function generarHtmlDocumentoImpresion(documento) {
    if (!documento) return '';

    // 1. Obtener Tema y Tipografía
    const tema = TEMAS_ZEN[documento.temaId] || TEMAS_ZEN.domus;
    const fuente = FUENTES_DISPONIBLES.find(f => f.id === documento.fuenteId) || FUENTES_DISPONIBLES[0];

    // 2. Tamaños Proporcionales de Texto por Sección (Contenido y Título con +4pt de delta)
    const fsEmisor = obtenerTamanoContenidoSeccion(documento, 'emisor');
    const fsTituloEmisor = obtenerTamanoTituloSeccion(documento, 'emisor');

    const fsCliente = obtenerTamanoContenidoSeccion(documento, 'cliente');
    const fsTituloCliente = obtenerTamanoTituloSeccion(documento, 'cliente');

    const fsCondiciones = obtenerTamanoContenidoSeccion(documento, 'condiciones');
    const fsTituloCondiciones = obtenerTamanoTituloSeccion(documento, 'condiciones');

    const fsPartidas = obtenerTamanoContenidoSeccion(documento, 'partidas');

    const fsTotales = obtenerTamanoContenidoSeccion(documento, 'totales');
    const fsTituloTotales = obtenerTamanoTituloSeccion(documento, 'totales');

    const fsBanco = obtenerTamanoContenidoSeccion(documento, 'banco');
    const fsTituloBanco = obtenerTamanoTituloSeccion(documento, 'banco');

    const fsNotaClave = obtenerTamanoContenidoSeccion(documento, 'notaClave');
    const fsTituloNotaClave = obtenerTamanoTituloSeccion(documento, 'notaClave');

    const fsFirmas = obtenerTamanoContenidoSeccion(documento, 'firmas');
    const fsTituloFirmas = obtenerTamanoTituloSeccion(documento, 'firmas');

    // 3. Cálculos Financieros
    const subtotal = (documento.conceptos || []).reduce((acc, curr) => {
        const cant = Number(curr.cantidad) || 0;
        const precio = Number(curr.precioUnitario) || 0;
        return acc + (cant * precio);
    }, 0);

    const descuento = Number(documento.descuento) || 0;
    const baseIva = Math.max(0, subtotal - descuento);
    const montoIva = documento.aplicaIva ? (baseIva * ((Number(documento.tasaIva) || 16) / 100)) : 0;
    const totalFinal = baseIva + montoIva;
    const importeEnLetras = numeroALetras(totalFinal, 'PESOS', 'M.N.');
    const tipoActual = (documento.tipoDocumento || 'presupuesto').toLowerCase();

    // 4. Parámetros de Hoja, Escala y Ajuste Inteligente
    const tamanoHoja = documento.tamanoHoja || 'letter';
    const ajusteImpresion = documento.ajusteImpresion || 'ajustar_1_hoja';

    const pageRule = tamanoHoja === 'legal' ? 'size: legal portrait;' :
                     tamanoHoja === 'a4' ? 'size: A4 portrait;' :
                     'size: letter portrait;';

    const isAjuste1Hoja = ajusteImpresion === 'ajustar_1_hoja' || ajusteImpresion === 'auto';
    const isCompacto90 = ajusteImpresion === 'compacto_90';
    const isCompacto85 = ajusteImpresion === 'compacto_85';

    const pageMargin = isAjuste1Hoja ? 'margin: 4mm 6mm 4mm 6mm;' :
                       isCompacto90 ? 'margin: 5mm 6mm 5mm 6mm;' :
                       isCompacto85 ? 'margin: 3.5mm 5mm 3.5mm 5mm;' :
                       'margin: 8mm 8mm 8mm 8mm;';

    const cardPadding = isAjuste1Hoja ? '1.5mm 2.5mm' : isCompacto85 ? '1.2mm 2mm' : '2.5mm 3.5mm';
    const cardGap = isAjuste1Hoja ? '2mm' : isCompacto85 ? '1.5mm' : '3.5mm';
    const tableCellPadding = isAjuste1Hoja ? '2px 3.2px' : isCompacto85 ? '1.5px 2.8px' : '3px 4px';
    const sectionMargin = isAjuste1Hoja ? '1.8mm' : isCompacto85 ? '1.2mm' : '2.8mm';

    // 5. Generar Filas de Partidas
    const conceptosHtml = (documento.conceptos || []).map((c) => {
        const cant = Number(c.cantidad) || 0;
        const unit = Number(c.precioUnitario) || 0;
        const totalPartida = cant * unit;
        const cEstilos = c.estilos || {};
        const docEstilos = documento.estilos || {};

        const fwTitle = cEstilos.fontWeight || docEstilos.fontWeight || '700';
        const fsStyle = cEstilos.fontStyle || docEstilos.fontStyle || 'normal';
        const tdStyle = cEstilos.textDecoration || docEstilos.textDecoration || 'none';
        const taStyle = cEstilos.textAlign || docEstilos.textAlign || 'left';

        return `
            <tr class="concept-row">
                <td class="col-cant text-center" style="font-size: ${fsPartidas}px; font-weight: 700; color: ${tema.primario};">
                    ${cant}
                </td>
                <td class="col-desc" style="font-size: ${fsPartidas}px; text-align: ${taStyle};">
                    <div class="concept-title" style="font-size: ${Math.round(fsPartidas * 1.05)}px; font-weight: ${fwTitle}; font-style: ${fsStyle}; text-decoration: ${tdStyle}; color: #0f172a; text-align: ${taStyle};">
                        ${formatearTextoEnriquecidoHtml(c.descripcion || '')}
                    </div>
                    ${c.subtitulo ? `
                        <div class="concept-sub" style="font-size: ${Math.round(fsPartidas * 0.92)}px; font-style: ${fsStyle}; text-align: ${taStyle}; color: #475569; margin-top: 2px; white-space: pre-wrap;">
                            ${formatearTextoEnriquecidoHtml(c.subtitulo)}
                        </div>
                    ` : ''}
                </td>
                <td class="col-periodo text-center" style="font-size: ${fsPartidas}px; color: #475569;">
                    ${escapeHtml(tipoActual === 'presupuesto' ? (c.unidad || 'Servicio') : (c.periodo || 'Único'))}
                </td>
                <td class="col-unit text-right" style="font-size: ${fsPartidas}px; white-space: nowrap; font-weight: 600; color: #334155;">
                    $${unit.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td class="col-importe text-right" style="font-size: ${fsPartidas}px; white-space: nowrap; font-weight: 700; color: #0f172a;">
                    $${totalPartida.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
            </tr>
        `;
    }).join('');

    // 6. Cuentas Bancarias
    const cuentasBancarias = Array.isArray(documento.cuentasBancarias) ? documento.cuentasBancarias : [];
    const bancosHtml = cuentasBancarias.length > 0 ? `
        <div class="doc-bank-box" style="font-size: ${fsBanco}pt; border-left: 3.5px solid ${tema.primario}; background: #f8fafc; padding: 2.5mm 3.5mm; margin-bottom: 3mm; border-radius: 4px; break-inside: avoid; page-break-inside: avoid;">
            <div style="font-size: ${fsTituloBanco}pt; font-weight: 700; color: ${tema.primario}; margin-bottom: 1.5mm;">
                Datos Bancarios para Depósito o Transferencia:
            </div>
            <div style="display: flex; flex-direction: column; gap: 4px; color: #334155; line-height: 1.35;">
                ${cuentasBancarias.map((cb, i) => `
                    <div style="${i < cuentasBancarias.length - 1 ? 'border-bottom: 1px dashed #cbd5e1; padding-bottom: 3px;' : ''}">
                        <strong>Banco:</strong> ${escapeHtml(cb.banco || 'Banco')} | 
                        <strong>Beneficiario:</strong> ${escapeHtml(cb.titular || cb.beneficiario || 'Domus Habita')} | 
                        <strong>CLABE:</strong> ${escapeHtml(cb.clabe || 'N/D')} 
                        ${cb.cuenta ? `| <strong>Cuenta:</strong> ${escapeHtml(cb.cuenta)}` : ''}
                        ${cb.tarjeta ? `| <strong>Tarjeta:</strong> ${escapeHtml(cb.tarjeta)}` : ''}
                    </div>
                `).join('')}
            </div>
        </div>
    ` : '';

    // 7. Nota Clave / Observaciones
    const notaClaveHtml = (documento.mostrarNotaClave !== false && documento.impactoComunitario) ? `
        <div class="doc-community-box" style="font-size: ${fsNotaClave}pt; border-left: 3.5px solid ${tema.acento}; background: #f0fdf4; padding: 2.5mm 3.5mm; margin-bottom: 3mm; border-radius: 4px; color: #166534; line-height: 1.4; break-inside: avoid; page-break-inside: avoid;">
            <strong style="font-size: ${fsTituloNotaClave}pt; color: #15803d; display: block; margin-bottom: 1mm;">
                ${escapeHtml(documento.tituloNotaClave || 'Nota Clave / Condiciones del Proyecto:')}
            </strong>
            ${formatearTextoEnriquecidoHtml(documento.impactoComunitario)}
        </div>
    ` : '';

    // 8. Firmas de Autorización
    const firmasHtml = `
        <div class="doc-signatures-wrap" style="font-size: ${fsFirmas}pt; margin-top: 3mm; margin-bottom: 2mm; break-inside: avoid; page-break-inside: avoid;">
            <div class="doc-sig-grid" style="display: grid; grid-template-columns: ${documento.incluirFirmaCliente !== false ? '1fr 1fr' : '1fr'}; gap: 24px; max-width: ${documento.incluirFirmaCliente !== false ? '100%' : '360px'}; margin: 0 auto;">
                <div class="doc-sig-cell" style="text-align: center;">
                    <div style="height: 38px; display: flex; align-items: flex-end; justify-content: center; margin-bottom: 2px;">
                        ${(documento.firmas?.emisorFirmaUrl || documento.firmaUrl) ? `
                            <img src="${escapeHtml(documento.firmas?.emisorFirmaUrl || documento.firmaUrl)}" alt="Firma Emisor" style="max-height: 38px; max-width: 130px; object-fit: contain;" />
                        ` : ''}
                    </div>
                    <div style="width: 85%; margin: 0 auto; height: 1.2px; background: #64748b; margin-bottom: 4px;"></div>
                    <div style="font-size: ${fsTituloFirmas}pt; font-weight: 700; color: #0f172a;">
                        ${escapeHtml(documento.firmas?.emisorNombre || 'C. Andrés Jonnatan Wong Feregrino')}
                    </div>
                    <div style="font-size: ${fsFirmas}pt; color: #64748b; margin-top: 1px;">
                        ${escapeHtml(documento.firmas?.emisorCargo || (tipoActual === 'pago' ? 'Receptor del Pago / Domus Habita Soluciones Integrales' : tipoActual === 'remision' ? 'Representante / Domus Habita Soluciones Integrales' : 'Prestador del Servicio / Domus Habita Soluciones Integrales'))}
                    </div>
                </div>

                ${documento.incluirFirmaCliente !== false ? `
                    <div class="doc-sig-cell" style="text-align: center;">
                        <div style="height: 38px; display: flex; align-items: flex-end; justify-content: center; margin-bottom: 2px;">
                            ${documento.firmas?.receptorFirmaUrl ? `
                                <img src="${escapeHtml(documento.firmas.receptorFirmaUrl)}" alt="Firma Receptor" style="max-height: 38px; max-width: 130px; object-fit: contain;" />
                            ` : ''}
                        </div>
                        <div style="width: 85%; margin: 0 auto; height: 1.2px; background: #64748b; margin-bottom: 4px;"></div>
                        <div style="font-size: ${fsTituloFirmas}pt; font-weight: 700; color: #0f172a;">
                            ${escapeHtml(documento.firmas?.receptorNombre || 'Representante Acreditado')}
                        </div>
                        <div style="font-size: ${fsFirmas}pt; color: #64748b; margin-top: 1px;">
                            ${escapeHtml(documento.firmas?.receptorCargo || (tipoActual === 'pago' ? 'Emisor del Pago / Acreditado' : tipoActual === 'remision' ? 'Recepción y Compromiso de Liquidación' : 'Aceptación y Visto Bueno del Condominio'))}
                        </div>
                    </div>
                ` : ''}
            </div>
        </div>
    `;

    // 8.5. Detalles de emisor combinados dinámicamente
    const partesDetallesEmisor = [
        documento.emisor?.direccion,
        documento.emisor?.contactoInfo,
        documento.emisor?.rfc ? `RFC: ${documento.emisor.rfc}` : null
    ].filter(Boolean);
    const emisorDetallesFinal = partesDetallesEmisor.length > 0 ? partesDetallesEmisor.join(' · ') : (documento.emisor?.detalles || '');

    // 9. Documento HTML Completo e Independiente
    const fullHtml = `<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${escapeHtml(documento.folio || 'Documento')} - ${escapeHtml(documento.tituloDocumento || 'Presupuesto')} - ${escapeHtml(documento.emisor?.nombre || 'Domus Habita')}</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Cinzel:wght@500;700;800&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600;700&family=Lato:ital,wght@0,400;0,700;1,400&family=Merriweather:ital,wght@0,400;0,700;1,400&family=Montserrat:ital,wght@0,400;0,600;0,700;0,800;1,400&family=Open+Sans:ital,wght@0,400;0,600;0,700;1,400&family=Playfair+Display:ital,wght@0,500;0,700;1,400&family=Poppins:ital,wght@0,400;0,600;0,700;1,400&family=Roboto:wght@400;500;700&display=swap" rel="stylesheet">
    <style>
        @page {
            ${pageRule}
            ${pageMargin}
        }

        * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            -webkit-font-smoothing: antialiased !important;
            -moz-osx-font-smoothing: grayscale !important;
            text-rendering: geometricPrecision !important;
            image-rendering: -webkit-optimize-contrast !important;
        }

        body {
            background: #ffffff;
            font-family: ${fuente.fontFamily};
            color: #1e293b;
            line-height: 1.3;
            width: 100%;
            height: auto;
            margin: 0;
            padding: 0;
            -webkit-font-smoothing: antialiased !important;
            -moz-osx-font-smoothing: grayscale !important;
            text-rendering: geometricPrecision !important;
        }

        .document-sheet {
            width: 100%;
            background: #ffffff;
            margin: 0;
            padding: 0;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
        }

        .doc-top-bar {
            height: 3.5px;
            background: ${tema.gradienteBarra};
            margin-bottom: 2mm;
            border-radius: 2px;
            width: 100%;
        }

        .doc-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding-bottom: 2mm;
            margin-bottom: 2.5mm;
            border-bottom: 1.2px solid #cbd5e1;
            gap: 12px;
            break-inside: avoid;
            page-break-inside: avoid;
        }

        .doc-emisor-block {
            display: flex;
            align-items: center;
            gap: 10px;
            flex: 1;
        }

        .doc-logo-img {
            max-width: 95px;
            max-height: 42px;
            object-fit: contain;
        }

        .doc-emisor-info h1 {
            font-size: ${fsTituloEmisor}pt;
            font-weight: 800;
            color: ${tema.primario};
            line-height: 1.1;
            margin: 0;
        }

        .doc-emisor-slogan {
            font-size: ${fsEmisor}pt;
            font-weight: 700;
            color: ${tema.secundario};
            margin: 1px 0;
        }

        .doc-emisor-details {
            font-size: ${Math.max(7, Math.round(fsEmisor * 0.9))}pt;
            color: #64748b;
            line-height: 1.25;
            white-space: pre-line;
        }

        .doc-meta-block {
            text-align: right;
            flex-shrink: 0;
        }

        .doc-status-badge {
            display: inline-block;
            background: ${tema.acento};
            color: #ffffff;
            font-weight: 800;
            font-size: ${Math.max(7, Math.round(fsEmisor * 0.85))}pt;
            letter-spacing: 0.5px;
            padding: 2px 7px;
            border-radius: 3px;
            text-transform: uppercase;
            margin-bottom: 2px;
        }

        .doc-main-title {
            font-size: ${fsTituloEmisor}pt;
            font-weight: 800;
            color: ${tema.primario};
            letter-spacing: 0.3px;
            margin-bottom: 2px;
        }

        .doc-meta-table {
            margin-left: auto;
            border-collapse: collapse;
            font-size: ${fsEmisor}pt;
        }

        .doc-meta-table td {
            padding: 0.5px 3px;
        }

        .doc-meta-table .label {
            color: #64748b;
            font-weight: 600;
            text-align: right;
        }

        .doc-meta-table .val {
            font-weight: 700;
            color: #0f172a;
            text-align: right;
        }

        .doc-info-grid {
            display: grid;
            grid-template-columns: 1.1fr 0.9fr;
            gap: ${cardGap};
            margin-bottom: ${sectionMargin};
            break-inside: avoid;
            page-break-inside: avoid;
        }

        .doc-info-card {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
            padding: ${cardPadding};
        }

        .doc-card-header {
            border-bottom: 1px solid #f1f5f9;
            padding-bottom: 1mm;
            margin-bottom: 1.5mm;
        }

        .doc-card-title {
            font-weight: 800;
            letter-spacing: 0.3px;
            text-transform: uppercase;
        }

        .doc-card-table {
            width: 100%;
            border-collapse: collapse;
        }

        .doc-card-table td {
            padding: 1px 0;
            vertical-align: top;
            line-height: 1.25;
        }

        .doc-card-table .d-label {
            color: #64748b;
            font-weight: 600;
            width: 30%;
        }

        .doc-card-table .d-val {
            color: #0f172a;
            width: 70%;
        }

        .doc-concepts-section {
            margin-bottom: ${sectionMargin};
        }

        .doc-section-title {
            font-size: ${Math.round(fsPartidas * 1.05)}px;
            font-weight: 800;
            color: #0f172a;
            letter-spacing: 0.3px;
            margin-bottom: 1.5mm;
        }

        .doc-concepts-table {
            width: 100%;
            border-collapse: collapse;
        }

        .doc-concepts-table thead {
            display: table-header-group;
        }

        .doc-concepts-table th {
            background: ${tema.tablaCabecera};
            color: ${tema.tablaTexto};
            padding: 2.5px 4px;
            font-size: ${Math.max(9, Math.round(fsPartidas * 0.95))}px;
            font-weight: 700;
            letter-spacing: 0.3px;
            text-transform: uppercase;
            border: none;
        }

        .doc-concepts-table td {
            padding: ${tableCellPadding};
            border-bottom: 1px solid #e2e8f0;
            vertical-align: middle;
        }

        .doc-concepts-table tr {
            break-inside: avoid;
            page-break-inside: avoid;
        }

        .col-cant { width: 7%; text-align: center; }
        .col-desc { width: 54%; text-align: left; }
        .col-periodo { width: 15%; text-align: center; }
        .col-unit { width: 12%; text-align: right; }
        .col-importe { width: 12%; text-align: right; }

        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .text-left { text-align: left; }

        .doc-summary-section {
            display: grid;
            grid-template-columns: 1.2fr 0.8fr;
            gap: ${cardGap};
            margin-bottom: ${sectionMargin};
            break-inside: avoid;
            page-break-inside: avoid;
        }

        .doc-amount-words-box {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
            padding: ${cardPadding};
            display: flex;
            flex-direction: column;
            justify-content: center;
        }

        .doc-amount-words-title {
            font-size: ${fsTituloTotales}pt;
            font-weight: 800;
            color: ${tema.primario};
            margin-bottom: 1mm;
        }

        .doc-amount-words {
            font-size: ${fsTotales}pt;
            font-weight: 700;
            color: #0f172a;
            line-height: 1.3;
        }

        .doc-note-small {
            font-size: ${Math.max(7, Math.round(fsTotales * 0.88))}pt;
            color: #64748b;
            margin-top: 1.5mm;
            line-height: 1.2;
        }

        .doc-totals-table {
            width: 100%;
            border-collapse: collapse;
            font-size: ${fsTotales}pt;
        }

        .doc-totals-table td {
            padding: 1.5px 3.5px;
        }

        .doc-totals-table .t-label {
            color: #475569;
            font-weight: 600;
            text-align: right;
        }

        .doc-totals-table .t-val {
            font-weight: 700;
            color: #0f172a;
            text-align: right;
            white-space: nowrap;
        }

        .doc-totals-table .total-row td {
            background: ${tema.totalFila};
            color: #ffffff;
            font-size: ${fsTituloTotales}pt;
            font-weight: 800;
            padding: 3px 5px;
            border-radius: 3px;
        }

        .doc-totals-table .total-row .t-val {
            color: ${tema.totalMonto};
        }

        .doc-footer {
            border-top: 1px solid #e2e8f0;
            padding-top: 1.5mm;
            margin-top: 2.5mm;
            display: flex;
            justify-content: space-between;
            font-size: 7.2px;
            color: #64748b;
            break-inside: avoid;
            page-break-inside: avoid;
        }
    </style>
</head>
<body>
    <div class="document-sheet">
        <div>
            <!-- Barra de acento superior Zen -->
            <div class="doc-top-bar"></div>

            <!-- Encabezado con datos del emisor y folio -->
            <header class="doc-header">
                <div class="doc-emisor-block">
                    ${(documento.emisor?.mostrarLogo1 && (documento.emisor?.logo1Url || documento.emisor?.logoUrl)) ? `
                        <img src="${escapeHtml(documento.emisor.logo1Url || documento.emisor.logoUrl)}" alt="Logo" class="doc-logo-img" />
                    ` : ''}
                    <div class="doc-emisor-info">
                        <h1>${escapeHtml(documento.emisor?.nombre || 'Domus Habita')}</h1>
                        <div class="doc-emisor-slogan">${escapeHtml(documento.emisor?.slogan || 'Soluciones Residenciales & Control de Accesos')}</div>
                        <div class="doc-emisor-details">${escapeHtml(emisorDetallesFinal)}</div>
                    </div>
                </div>

                <div class="doc-meta-block">
                    <span class="doc-status-badge" style="background: ${tipoActual === 'pago' ? '#16a34a' : tipoActual === 'remision' ? '#d97706' : tema.acento}; color: #ffffff;">
                        ${escapeHtml(documento.badgeEstado || (tipoActual === 'pago' ? '✓ PAGADO EN EFECTIVO' : tipoActual === 'remision' ? 'PENDIENTE DE PAGO' : 'VIGENTE'))}
                    </span>
                    <div class="doc-main-title" style="color: ${tema.primario}; font-size: ${fsTituloEmisor}pt;">
                        ${escapeHtml(documento.tituloDocumento || (tipoActual === 'pago' ? 'NOTA DE PAGO' : tipoActual === 'remision' ? 'REMISIÓN DE COBRO' : 'PRESUPUESTO FORMAL'))}
                    </div>
                    <table class="doc-meta-table">
                        <tbody>
                            <tr>
                                <td class="label">Folio:</td>
                                <td class="val" style="color: ${tema.primario}; font-weight: 700;">${escapeHtml(documento.folio || 'S/F')}</td>
                            </tr>
                            ${tipoActual === 'pago' ? `
                                <tr>
                                    <td class="label">Fecha de Expedición:</td>
                                    <td class="val">${escapeHtml(documento.fechaEmision || '')}</td>
                                </tr>
                                <tr>
                                    <td class="label">Fecha de Pago:</td>
                                    <td class="val" style="color: #16a34a; font-weight: 700;">${escapeHtml(documento.fechaPago || documento.fechaEmision || '')}</td>
                                </tr>
                                <tr>
                                    <td class="label">Estatus Saldo:</td>
                                    <td class="val" style="color: #16a34a; font-weight: 700;">$0.00 Adeudo (Saldado 100%)</td>
                                </tr>
                            ` : tipoActual === 'remision' ? `
                                <tr>
                                    <td class="label">Fecha de Emisión:</td>
                                    <td class="val">${escapeHtml(documento.fechaEmision || '')}</td>
                                </tr>
                                <tr>
                                    <td class="label">Fecha Límite / Vencimiento:</td>
                                    <td class="val" style="color: #dc2626; font-weight: 700;">${escapeHtml(documento.fechaVencimiento || documento.fechaVigencia || '5 días hábiles')}</td>
                                </tr>
                                <tr>
                                    <td class="label">Estatus Saldo:</td>
                                    <td class="val" style="color: #d97706; font-weight: 700;">Pendiente de Liquidación</td>
                                </tr>
                            ` : `
                                <tr>
                                    <td class="label">Fecha de Emisión:</td>
                                    <td class="val">${escapeHtml(documento.fechaEmision || '')}</td>
                                </tr>
                                <tr>
                                    <td class="label">Vigencia:</td>
                                    <td class="val" style="font-weight: 600;">${escapeHtml(documento.fechaVigencia || documento.fechaVencimiento || '15 días naturales')}</td>
                                </tr>
                            `}
                            <tr>
                                <td class="label">Moneda:</td>
                                <td class="val">${escapeHtml(documento.moneda || 'MXN - Pesos Mexicanos')}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </header>

            <!-- Datos del Cliente y Condiciones -->
            <section class="doc-info-grid">
                <div class="doc-info-card" style="border-left: 3.5px solid ${tema.primario}; font-size: ${fsCliente}pt;">
                    <div class="doc-card-header">
                        <span class="doc-card-title" style="color: ${tema.primario}; font-size: ${fsCliente + 4}pt;">
                            ${tipoActual === 'pago' ? 'CLIENTE / PAGADOR ACREDITADO' : tipoActual === 'remision' ? 'CLIENTE / DEUDOR DEL SERVICIO' : 'CLIENTE / RECEPTOR DEL SERVICIO'}
                        </span>
                    </div>
                    <table class="doc-card-table" style="font-size: ${fsCliente}pt;">
                        <tbody>
                            <tr>
                                <td class="d-label">Nombre / Razón:</td>
                                <td class="d-val"><strong>${escapeHtml(documento.cliente?.nombre || 'Cliente General')}</strong></td>
                            </tr>
                            ${documento.cliente?.contacto ? `
                                <tr>
                                    <td class="d-label">Atención a:</td>
                                    <td class="d-val">${escapeHtml(documento.cliente.contacto)}</td>
                                </tr>
                            ` : ''}
                            ${documento.cliente?.direccion ? `
                                <tr>
                                    <td class="d-label">Dirección:</td>
                                    <td class="d-val">${escapeHtml(documento.cliente.direccion)}</td>
                                </tr>
                            ` : ''}
                            ${documento.cliente?.municipioEstado ? `
                                <tr>
                                    <td class="d-label">Ubicación:</td>
                                    <td class="d-val">${escapeHtml(documento.cliente.municipioEstado)}</td>
                                </tr>
                            ` : ''}
                            ${documento.cliente?.telefono ? `
                                <tr>
                                    <td class="d-label">Teléfono:</td>
                                    <td class="d-val">${escapeHtml(documento.cliente.telefono)}</td>
                                </tr>
                            ` : ''}
                            ${documento.cliente?.email ? `
                                <tr>
                                    <td class="d-label">Correo:</td>
                                    <td class="d-val">${escapeHtml(documento.cliente.email)}</td>
                                </tr>
                            ` : ''}
                        </tbody>
                    </table>
                </div>

                <div class="doc-info-card" style="border-left: 3.5px solid ${tema.secundario}; font-size: ${fsCondiciones}pt;">
                    <div class="doc-card-header">
                        <span class="doc-card-title" style="color: ${tema.secundario}; font-size: ${fsCondiciones + 4}pt;">
                            ${tipoActual === 'pago' 
                                ? 'MODALIDAD DEL PAGO RECIBIDO' 
                                : tipoActual === 'remision' 
                                    ? 'CONDICIONES DE COBRO Y RECEPCIÓN' 
                                    : 'CONDICIONES COMERCIALES Y DE SERVICIO'}
                        </span>
                    </div>
                    <table class="doc-card-table" style="font-size: ${fsCondiciones}pt;">
                        <tbody>
                            ${tipoActual === 'presupuesto' ? `
                                ${(documento.condiciones?.formaPago || documento.metodoPago || documento.condiciones?.terminosPago) ? `
                                    <tr>
                                        <td class="d-label">Forma de Pago:</td>
                                        <td class="d-val">${escapeHtml(documento.condiciones?.formaPago || documento.metodoPago || documento.condiciones?.terminosPago)}</td>
                                    </tr>
                                ` : ''}
                                ${documento.condiciones?.inversionInicial ? `
                                    <tr>
                                        <td class="d-label">Inversión Inicial:</td>
                                        <td class="d-val">${escapeHtml(sincronizarTextoInversionInicial(documento.condiciones.inversionInicial, totalFinal))}</td>
                                    </tr>
                                ` : ''}
                                ${(documento.condiciones?.tiempoEntrega || documento.condiciones?.plazoInstalacion) ? `
                                    <tr>
                                        <td class="d-label">Plazo de Instalación:</td>
                                        <td class="d-val">${escapeHtml(documento.condiciones?.tiempoEntrega || documento.condiciones?.plazoInstalacion)}</td>
                                    </tr>
                                ` : ''}
                                ${documento.condiciones?.rentaSistemas ? `
                                    <tr>
                                        <td class="d-label">Renta de Sistemas:</td>
                                        <td class="d-val">${escapeHtml(documento.condiciones.rentaSistemas)}</td>
                                    </tr>
                                ` : ''}
                                ${documento.condiciones?.garantia ? `
                                    <tr>
                                        <td class="d-label">Beneficio / Garantía:</td>
                                        <td class="d-val">${escapeHtml(documento.condiciones.garantia)}</td>
                                    </tr>
                                ` : ''}
                                ${(documento.condiciones?.periodo && documento.condiciones.periodo !== 'N/A') ? `
                                    <tr>
                                        <td class="d-label">Periodo:</td>
                                        <td class="d-val">${escapeHtml(documento.condiciones.periodo)}</td>
                                    </tr>
                                ` : ''}
                            ` : tipoActual === 'pago' ? `
                                ${(documento.metodoPago || documento.condiciones?.terminosPago) ? `
                                    <tr>
                                        <td class="d-label">Método de Pago:</td>
                                        <td class="d-val">${escapeHtml(documento.metodoPago || documento.condiciones?.terminosPago)}</td>
                                    </tr>
                                ` : ''}
                                ${documento.referenciaBancaria ? `
                                    <tr>
                                        <td class="d-label">Ref. Bancaria:</td>
                                        <td class="d-val"><strong>${escapeHtml(documento.referenciaBancaria)}</strong></td>
                                    </tr>
                                ` : ''}
                                ${documento.condiciones?.periodo ? `
                                    <tr>
                                        <td class="d-label">Periodo Liquidado:</td>
                                        <td class="d-val">${escapeHtml(documento.condiciones.periodo)}</td>
                                    </tr>
                                ` : ''}
                                ${documento.condiciones?.garantia ? `
                                    <tr>
                                        <td class="d-label">Finiquito / Estatus:</td>
                                        <td class="d-val">${escapeHtml(documento.condiciones.garantia)}</td>
                                    </tr>
                                ` : ''}
                            ` : `
                                ${documento.condiciones?.periodo ? `
                                    <tr>
                                        <td class="d-label">Periodo de Servicio:</td>
                                        <td class="d-val">${escapeHtml(documento.condiciones.periodo)}</td>
                                    </tr>
                                ` : ''}
                                ${documento.condiciones?.terminosPago ? `
                                    <tr>
                                        <td class="d-label">Liquidación:</td>
                                        <td class="d-val">${escapeHtml(documento.condiciones.terminosPago)}</td>
                                    </tr>
                                ` : ''}
                                ${documento.condiciones?.tiempoEntrega ? `
                                    <tr>
                                        <td class="d-label">Plazo / Entrega:</td>
                                        <td class="d-val">${escapeHtml(documento.condiciones.tiempoEntrega)}</td>
                                    </tr>
                                ` : ''}
                                ${documento.condiciones?.garantia ? `
                                    <tr>
                                        <td class="d-label">Estatus:</td>
                                        <td class="d-val">${escapeHtml(documento.condiciones.garantia)}</td>
                                    </tr>
                                ` : ''}
                            `}
                        </tbody>
                    </table>
                </div>
            </section>

            <!-- Desglose de Partidas -->
            <section class="doc-concepts-section">
                <div class="doc-section-title">
                    ${tipoActual === 'pago' 
                        ? 'DESGLOSE DE SERVICIOS CUBIERTOS Y LIQUIDADOS' 
                        : tipoActual === 'remision' 
                            ? 'DESGLOSE DE SERVICIOS PRESTADOS Y CONCLUIDOS' 
                            : 'DESGLOSE DE PARTIDAS, MATERIALES Y SERVICIOS'}
                </div>
                <table class="doc-concepts-table">
                    <thead>
                        <tr>
                            <th class="col-cant">CANT.</th>
                            <th class="col-desc">DESCRIPCIÓN DE LA PARTIDA O SERVICIO</th>
                            <th class="col-periodo">${tipoActual === 'presupuesto' ? 'UNIDAD DE MEDIDA' : 'PERIODO'}</th>
                            <th class="col-unit">PRECIO UNIT. ($)</th>
                            <th class="col-importe">IMPORTE</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${conceptosHtml}
                    </tbody>
                </table>
            </section>

            <!-- Resumen Financiero e Importe en Letras -->
            <section class="doc-summary-section">
                <div class="doc-amount-words-box">
                    <div class="doc-amount-words-title">IMPORTE TOTAL CON LETRA:</div>
                    <div class="doc-amount-words">${escapeHtml(importeEnLetras)}</div>
                    <div class="doc-note-small">
                        ${tipoActual === 'pago' 
                            ? 'Comprobante de pago con finiquito total. Las especificaciones amparadas quedan respaldadas bajo el folio oficial de esta nota.' 
                            : tipoActual === 'remision' 
                                ? 'Remisión de cobro sujeta a liquidación antes de la fecha límite estipulada.' 
                                : 'Precios expresados en moneda nacional (MXN). Las especificaciones y costos quedan respaldados bajo el folio oficial de este documento.'}
                    </div>
                </div>

                <div>
                    <table class="doc-totals-table">
                        <tbody>
                            <tr>
                                <td class="t-label">Subtotal:</td>
                                <td class="t-val">$${subtotal.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                            ${descuento > 0 ? `
                                <tr>
                                    <td class="t-label" style="color: #16a34a;">Descuento / Bonificación:</td>
                                    <td class="t-val" style="color: #16a34a;">-$${descuento.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                </tr>
                            ` : ''}
                            ${documento.aplicaIva ? `
                                <tr>
                                    <td class="t-label">IVA (${documento.tasaIva || 16}%):</td>
                                    <td class="t-val">$${montoIva.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                </tr>
                            ` : ''}
                            <tr class="total-row">
                                <td class="t-label">${tipoActual === 'pago' ? 'TOTAL PAGADO:' : tipoActual === 'remision' ? 'TOTAL A LIQUIDAR:' : 'TOTAL:'}</td>
                                <td class="t-val">$${totalFinal.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MXN</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <!-- Nota Clave / Observaciones -->
            ${notaClaveHtml}

            <!-- Datos Bancarios -->
            ${bancosHtml}

            <!-- Firmas de Autorización -->
            ${firmasHtml}
        </div>

        <!-- Pie de Página -->
        <footer class="doc-footer">
            <div>${escapeHtml(documento.emisor?.nombre || 'Domus Habita')} — Folio Oficial: ${escapeHtml(documento.folio || 'S/F')}</div>
            <div>Página 1 de 1</div>
            <div>Generado con Domus Habita Cloud | ${new Date().toLocaleDateString('es-MX')}</div>
        </footer>
    </div>
</body>
</html>`;

    return fullHtml;
}

export function imprimirDocumentoComercial(documento) {
    if (!documento) return;
    const fullHtml = generarHtmlDocumentoImpresion(documento);
    if (!fullHtml) return;

    // 10. Inyectar en un Iframe Oculto e Invocar Impresión
    try {
        const frameId = 'print-commercial-doc-iframe';
        let iframe = document.getElementById(frameId);
        if (iframe && iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
        }

        iframe = document.createElement('iframe');
        iframe.id = frameId;
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.style.opacity = '0';
        iframe.style.pointerEvents = 'none';
        iframe.style.zIndex = '-9999';

        document.body.appendChild(iframe);

        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(fullHtml);
        iframeDoc.close();

        // Esperar a que el iframe descargue las fuentes y renderice imágenes antes de mandar a imprimir (Punto 1)
        const dispararImpresion = async () => {
            try {
                if (iframeDoc.fonts && iframeDoc.fonts.ready) {
                    await iframeDoc.fonts.ready;
                }
            } catch (fontErr) {
                console.warn('Aviso al esperar carga de fuentes:', fontErr);
            }

            // Pausa breve para garantizar composición vectorial completa de la página
            setTimeout(() => {
                try {
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                } catch (err) {
                    console.error('Error al invocar iframe print:', err);
                    window.print();
                }

                // Limpieza del iframe tras concluir la interacción
                setTimeout(() => {
                    if (iframe && iframe.parentNode) {
                        iframe.parentNode.removeChild(iframe);
                    }
                }, 15000);
            }, 300);
        };

        if (iframeDoc.readyState === 'complete') {
            dispararImpresion();
        } else {
            iframe.onload = dispararImpresion;
        }

    } catch (e) {
        console.error('Fallo al crear iframe de impresión, usando window.print fallback:', e);
        window.print();
    }
}
